import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Filter16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';
import { Button } from 'carbon-components-react';
import Panel from './panel';
import Utils from 'utilities/helper.util';
import {
  setEnquirySearchFilter,
  setForwardEnquiryDateRangeText
} from 'redux/enquiries';
import { Dayjs } from 'dayjs';
import { useComponentMount } from 'utilities/hooks.util';
import { isSafari, isMobile } from 'react-device-detect';
import { setMapSearchWithExtentZoom } from 'redux/map/actions';
import { setDashboardUIStateAdminRangeText } from 'redux/dashboard/actions';

type initialValuesType = {
  enquiryLodged: string;
  jobStart: string;
  enquiryLodgedCustomed: boolean;
  jobStartCustomed: boolean;
  createdAfter: Dayjs;
  createdBefore: Dayjs | undefined;
  digStartAfter: Dayjs | undefined;
  digStartBefore: Dayjs | undefined;
  status: string[];
  userReference: string;
  address: string;
  isAutoRenewPending: boolean;
  requestorFullName: string;
  requestorEmail: string;
  isSandboxTest?: boolean;
  jobNumber: string;
  orgTenant?: string;
};

const initialValues: initialValuesType = {
  enquiryLodged: '60',
  jobStart: '',
  enquiryLodgedCustomed: false,
  jobStartCustomed: false,
  createdAfter: Utils.getDateBeforeDays(60).startOf('day'),
  createdBefore: undefined,
  digStartAfter: undefined,
  digStartBefore: undefined,
  status: [],
  userReference: '',
  address: '',
  isAutoRenewPending: false,
  requestorFullName: '',
  requestorEmail: '',
  isSandboxTest: false,
  jobNumber: ''
};

const dateRangeTextValueTransform: any = (dateRangeText: string) => {
  switch (dateRangeText) {
    case 'today':
      return 'today';
    case 'this-week':
      return 'this-week';
    case 'last-7-days':
      return '7';
    case 'last-30-days':
      return '30';
  }

  return;
};

const SearchFilter: React.FC<Props> = (props) => {
  const {
    isFullSet,
    buttonKind,
    buttonClassName,
    buttonDisabled,
    buttonIconDescription,
    buttonTooltipPosition,
    disabledAllExceptApply,
    onApply,
    onOpen,
    open,
    dateRangeText,
    user,
    mainContainerRef,
    isListView
  } = props;

  useEffect(() => {
    if (!isFullSet) {
      initialValues.enquiryLodged = '60';
      initialValues.createdAfter = Utils.getDateBeforeDays(60).startOf('day');
      initialValues.createdBefore = undefined;
    } else {
      switch (dateRangeText) {
        case 'today':
          initialValues.enquiryLodged = 'today';
          initialValues.createdAfter = Utils.getNewDate().startOf('day');
          initialValues.createdBefore = Utils.getNewDate().endOf('day');
          break;
        case 'this-week': {
          initialValues.enquiryLodged = 'this-week';
          const today = Utils.getNewDate();
          const startOfThisWeek = today.startOf('week').add(1, 'day');
          const endOfThisWeek = today.endOf('week').add(1, 'day');
          initialValues.createdAfter = startOfThisWeek;
          initialValues.createdBefore = endOfThisWeek;
          break;
        }
        case 'last-7-days':
          initialValues.enquiryLodged = '7';
          initialValues.createdAfter = Utils.getDateBeforeDays(6).startOf(
            'day'
          );
          break;
        case 'last-30-days':
          initialValues.enquiryLodged = '30';
          initialValues.createdAfter = Utils.getDateBeforeDays(29).startOf(
            'day'
          );
          break;
        default:
          initialValues.enquiryLodged = 'today';
          initialValues.createdAfter = Utils.getNewDate().startOf('day');
          initialValues.createdBefore = Utils.getNewDate().endOf('day');
          break;
      }
    }
  }, [isFullSet, dateRangeText]);

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<Boolean>(false);
  const [filterValues, setFilterValues] = useState<initialValuesType>(
    initialValues
  );
  const [filterJson, setfilterJson] = useState<string>('');
  const [parentOverflow, setParentOverflow] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const convertFilterValues = (
    filterValues: initialValuesType,
    initialDateRangeText?: string | undefined | null
  ) => {
    const {
      createdAfter,
      createdBefore,
      digStartAfter,
      digStartBefore,
      status,
      userReference,
      address,
      isAutoRenewPending,
      requestorFullName,
      requestorEmail,
      isSandboxTest,
      jobNumber,
      orgTenant
      // enquiryLodged
    } = filterValues;

    let createdAfterValue = createdAfter
      ? createdAfter.startOf('day').utc().format()
      : '';
    let createdBeforeValue = createdBefore
      ? createdBefore.endOf('day').utc().format()
      : '';

    if (initialDateRangeText) {
      switch (initialDateRangeText) {
        case 'today':
          createdAfterValue = Utils.getNewDate().startOf('day').utc().format();
          break;
        case 'this-week': {
          const today = Utils.getNewDate();
          const startOfThisWeek = today.startOf('week').add(1, 'day');
          const endOfThisWeek = today.endOf('week').add(1, 'day');
          createdAfterValue = startOfThisWeek.utc().format();
          createdBeforeValue = endOfThisWeek.utc().format();
          break;
        }
        case 'last-7-days':
          createdAfterValue = Utils.getDateBeforeDays(6)
            .startOf('day')
            .utc()
            .format();
          break;
        case 'last-30-days':
          createdAfterValue = Utils.getDateBeforeDays(29)
            .startOf('day')
            .utc()
            .format();
          break;
      }
    }

    return {
      createdAfter: createdAfterValue,
      createdBefore: createdBeforeValue,
      digStartAfter: digStartAfter
        ? digStartAfter.startOf('day').utc().format()
        : '',
      digStartBefore: digStartBefore
        ? digStartBefore.endOf('day').utc().format()
        : '',
      userReference,
      address,
      isAutoRenewPending,
      status,
      requestorFullName,
      requestorEmail,
      isSandboxTest,
      tenantId: orgTenant ? Number(orgTenant) : undefined,
      externalId: jobNumber
    };
  };

  const resetFilterValues = () => {
    const values = convertFilterValues(initialValues);
    !isListView && dispatch(setMapSearchWithExtentZoom([false]));
    setfilterJson(JSON.stringify(values));
    setFilterValues({
      ...initialValues,
      status: []
    });
    setIsOpen(!isOpen);
  };

  const applyFilter = (updateValues: any, zoomWithExtent: boolean = false) => {
    dispatch(setDashboardUIStateAdminRangeText(''));
    const values = convertFilterValues(updateValues);
    !isListView && dispatch(setMapSearchWithExtentZoom([zoomWithExtent]));
    setfilterJson(JSON.stringify(values));
    setFilterValues(updateValues);
    setIsOpen(!isOpen);
    if (typeof onApply === 'function') {
      onApply();
    }
  };

  const onClickOpenButton = () => {
    const filterButtonElement = filterButtonRef.current;
    filterButtonElement && filterButtonElement.blur();

    if (typeof open !== 'boolean') {
      setIsOpen(!isOpen);
    }
    if (typeof onOpen === 'function') {
      onOpen();
    }
  };

  const checkParentOverflow = useCallback(() => {
    const mainContainerElement = mainContainerRef.current;
    if (mainContainerElement) {
      if (
        mainContainerElement.scrollHeight > mainContainerElement.clientHeight
      ) {
        setParentOverflow(true);
      }
    }
  }, [mainContainerRef, setParentOverflow]);

  useEffect(() => {
    if (typeof open === 'boolean') {
      setIsOpen(open);
    }
  }, [open]);

  useEffect(() => {
    filterJson && dispatch(setEnquirySearchFilter(JSON.parse(filterJson)));
  }, [filterJson, dispatch]);

  useEffect(() => {
    if (isOpen && !isMobile && !isSafari) {
      checkParentOverflow();
    } else {
      setParentOverflow(false);
    }
  }, [isOpen, checkParentOverflow, setParentOverflow]);

  useEffect(() => {
    if (isListView) {
      dispatch(setMapSearchWithExtentZoom([false]));
    }
  }, [isListView, dispatch]);

  useComponentMount(() => {
    const values = convertFilterValues(filterValues, dateRangeText);
    setfilterJson(JSON.stringify(values));
    return () => {
      dispatch(setMapSearchWithExtentZoom([true]));
      dispatch(setEnquirySearchFilter(undefined));
    };
  });

  return (
    <div className="history-page--search-filter-container">
      <Button
        renderIcon={Filter16}
        iconDescription={buttonIconDescription}
        tooltipPosition={buttonTooltipPosition}
        hasIconOnly
        size="field"
        kind={buttonKind}
        disabled={buttonDisabled}
        className={buttonClassName}
        onClick={onClickOpenButton}
        ref={filterButtonRef}
      />
      {isOpen && (
        <Panel
          isFullSet={isFullSet}
          handleFilter={applyFilter}
          filterValues={filterValues}
          resetValues={resetFilterValues}
          setFilterValues={(v: any) => {
            dateRangeText && dispatch(setForwardEnquiryDateRangeText(''));
            setFilterValues(v);
          }}
          disabledAllExceptApply={disabledAllExceptApply}
          handleOpenCloseFilter={(open: boolean) => setIsOpen(open)}
          dateRangeText={dateRangeTextValueTransform(dateRangeText)}
          user={user}
          parentOverflow={parentOverflow}
          isListView={isListView}
        />
      )}
    </div>
  );
};

const propTypes = {
  isFullSet: PropTypes.bool,
  buttonKind: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttonIconDescription: PropTypes.string,
  buttonTooltipPosition: PropTypes.string,
  onApply: PropTypes.func,
  disabledAllExceptApply: PropTypes.bool,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  dateRangeText: PropTypes.string,
  user: PropTypes.any,
  mainContainerRef: PropTypes.any,
  isListView: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

SearchFilter.defaultProps = {
  buttonKind: 'tertiary',
  buttonDisabled: false,
  buttonClassName: '',
  buttonIconDescription: 'Filter',
  buttonTooltipPosition: 'bottom',
  onOpen: () => {}
};

export default React.memo(SearchFilter);
